import { action, computed, makeObservable, observable } from 'mobx';
import type { ObservableMap } from 'mobx';

import { isObjectEmpty } from '../../../shared/utils';
import type { TRecommendationRowData } from '../types';

import type { Recommendation } from './Recommendation.entity';

const staticRequiredFields: TRecommendationRowDataFields[] = ['name', 'indexFormat', 'dataType', 'paramValue'];
const requiredFieldErrorKey = 'alertsRecommendations:errors.requiredField';

type TRecommendationRowDataFields = keyof TRecommendationRowData;

type TErrorsObject = Partial<Record<TRecommendationRowDataFields, string[]>>;

export class RecommendationsValidationService {
  readonly _errors = observable.map<string, TErrorsObject>();

  constructor() {
    makeObservable(this);
  }

  private addError(field: TRecommendationRowDataFields, item: Recommendation, errorKey: string): void {
    const errorObject = this._errors.get(item.entityId) ?? {};
    const errorsSet = new Set(errorObject[field] ?? []);
    errorsSet.add(errorKey);
    this._errors.set(item.entityId, { ...errorObject, [field]: Array.from(errorsSet) });
  }

  private removeError(field: TRecommendationRowDataFields, item: Recommendation, errorKey: string): void {
    const errorObject = this._errors.get(item.entityId) ?? {};
    const errorsSet = new Set(errorObject[field] ?? []);
    errorsSet.delete(errorKey);
    this._errors.set(item.entityId, { ...errorObject, [field]: Array.from(errorsSet) });
  }

  private removeErrors(field: TRecommendationRowDataFields, item: Recommendation): void {
    const errorObject = this._errors.get(item.entityId) ?? {};
    this._errors.set(item.entityId, { ...errorObject, [field]: [] });
  }

  @computed
  get errors(): ObservableMap<string, TErrorsObject> {
    return this._errors;
  }

  private checkFieldRequired(field: TRecommendationRowDataFields, item: Recommendation): void {
    const errorObject = this._errors.get(item.entityId);

    if (item.data[field] != null) {
      if (!errorObject || isObjectEmpty(errorObject)) {
        return;
      }
      this.removeError(field, item, requiredFieldErrorKey);
      return;
    }

    if (staticRequiredFields.includes(field)) {
      this.addError(field, item, requiredFieldErrorKey);
    }
  }

  @action.bound
  validateRecommendationField(field: TRecommendationRowDataFields, item: Recommendation): boolean {
    this.removeErrors(field, item);
    this.checkFieldRequired(field, item);
    return !!this._errors.get(item.entityId)?.[field]?.length;
  }

  validateRecommendationItem(item: Recommendation): boolean {
    let hasErrors = false;

    for (const field of staticRequiredFields) {
      const hasFieldErrors = this.validateRecommendationField(field, item);
      if (hasFieldErrors && !hasErrors) hasErrors = hasFieldErrors;
    }

    return hasErrors;
  }

  validateRecommendations(items: Recommendation[]): boolean {
    let hasErrors = false;
    for (const item of items) {
      const hasItemErrors = this.validateRecommendationItem(item);
      if (hasItemErrors && !hasErrors) hasErrors = hasItemErrors;
    }
    return hasErrors;
  }
}
